import { useRef, useEffect } from 'react'
import Inputmask from 'inputmask'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { isValid as isValidCpf } from '@fnando/cpf'
import { isValid as isValidCnpj } from '@fnando/cnpj'
import { Button, Input, Select, Radio, notification } from 'antd'
import styled from '@emotion/styled'
import { useHistory, useParams } from 'react-router-dom'
import { ParceiroDetailStore } from './parceiroDetailStore'
import { createParceiro, updateParceiro, fetchCnpjFromBrasilApi } from './parceirosApi'

const UFS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'] // prettier-ignore

function formatCpfCnpj(value: string): string {
  const d = value.replace(/\D/g, '')
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  return value
}

function buildValidationSchema(documentMode: 'CNPJ' | 'CPF', isNew: boolean) {
  const cpfCnpj = isNew
    ? Yup.string()
        .required('Campo obrigatório')
        .test(
          'cpfCnpjValido',
          documentMode === 'CNPJ' ? 'CNPJ inválido' : 'CPF inválido',
          (value) => (documentMode === 'CNPJ' ? isValidCnpj(value ?? '') : isValidCpf(value ?? ''))
        )
    : Yup.string()

  return Yup.object().shape({
    cpfCnpj,
    nome: Yup.string().trim().required('Campo obrigatório'),
  })
}

function formatTelefone(value: string): string {
  const d = value.replace(/\D/g, '')
  if (d.length === 10) return d.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
  if (d.length === 11) return d.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
  return value
}

export function DadosTab() {
  const { documentMode, parceiro } = ParceiroDetailStore.useState()
  const dispatch = ParceiroDetailStore.useDispatch()
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const isNew = id === 'novo'
  const cpfCnpjRef = useRef<any>(null)
  const telefoneRef = useRef<any>(null)

  useEffect(() => {
    if (!cpfCnpjRef.current) return
    const el = cpfCnpjRef.current.input || cpfCnpjRef.current
    Inputmask.remove(el)
    const mask = documentMode === 'CNPJ' ? '99.999.999/9999-99' : '999.999.999-99'
    Inputmask({ mask }).mask(el)
  }, [documentMode, parceiro.cpfCnpj])

  useEffect(() => {
    if (!telefoneRef.current) return
    const el = telefoneRef.current.input || telefoneRef.current
    Inputmask({ mask: ['(99) 9999-9999', '(99) 99999-9999'], keepStatic: true }).mask(el)
  }, [])

  async function handleCnpjChange(
    maskedValue: string,
    setFieldValue: (field: string, value: any) => void
  ) {
    if (documentMode !== 'CNPJ') return
    const digits = maskedValue.replace(/\D/g, '')
    if (digits.length !== 14) return
    try {
      const data = await fetchCnpjFromBrasilApi(digits)
      setFieldValue('nome', data.razao_social ?? '')
      setFieldValue('telefone', formatTelefone(data.ddd_telefone_1 ?? ''))
      setFieldValue('uf', data.uf ?? '')
      if (data.qsa?.length) {
        dispatch(ParceiroDetailStore.actions.setQsaPrefill(data.qsa))
      }
    } catch {
      // brasilapi failure is silent
    }
  }

  async function handleSubmit(values: any) {
    try {
      if (isNew) {
        const { id: newId } = await createParceiro({
          cpfCnpj: formatCpfCnpj(values.cpfCnpj),
          nome: values.nome,
          telefone: values.telefone,
          uf: values.uf,
        })
        notification.success({ message: 'Parceiro criado com sucesso' })
        history.replace(`/parceiros/${newId}`)
      } else {
        await updateParceiro(Number(id), {
          nome: values.nome,
          telefone: values.telefone,
          uf: values.uf,
        })
        notification.success({ message: 'Parceiro atualizado com sucesso' })
      }
    } catch (err: any) {
      notification.error({ message: err?.response?.data?.message ?? 'Erro ao salvar parceiro' })
    }
  }

  const initialValues = {
    cpfCnpj: formatCpfCnpj(parceiro.cpfCnpj ?? ''),
    nome: parceiro.nome ?? '',
    telefone: formatTelefone(parceiro.telefone ?? ''),
    uf: parceiro.uf ?? '',
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={buildValidationSchema(documentMode, isNew)}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue }) => (
        <Form>
          <FormLayout>
            <DocumentToggle />
            <FieldGroup>
              <FieldLabel>{documentMode === 'CNPJ' ? 'CNPJ' : 'CPF'}</FieldLabel>
              <Field name="cpfCnpj">
                {({ field }: any) => (
                  <Input
                    ref={cpfCnpjRef}
                    {...field}
                    placeholder={documentMode === 'CNPJ' ? '00.000.000/0001-00' : '000.000.000-00'}
                    disabled={!isNew}
                    onChange={(e) => {
                      field.onChange(e)
                      handleCnpjChange(e.target.value, setFieldValue)
                    }}
                  />
                )}
              </Field>
              <ErrorMessage name="cpfCnpj" component={FieldError} />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Nome</FieldLabel>
              <Field name="nome" as={Input} placeholder="Razão Social / Nome" />
              <ErrorMessage name="nome" component={FieldError} />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Telefone</FieldLabel>
              <Field name="telefone">
                {({ field }: any) => (
                  <Input ref={telefoneRef} {...field} placeholder="(11) 99999-9999" />
                )}
              </Field>
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>UF</FieldLabel>
              <Select
                value={values.uf || undefined}
                onChange={(value) => setFieldValue('uf', value)}
                placeholder="Selecione a UF"
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="value"
                options={UFS.map((uf) => ({ value: uf, label: uf }))}
              />
            </FieldGroup>
            <SaveButton type="primary" htmlType="submit">
              Salvar
            </SaveButton>
          </FormLayout>
        </Form>
      )}
    </Formik>
  )
}

function DocumentToggle() {
  const { documentMode } = ParceiroDetailStore.useState()
  const dispatch = ParceiroDetailStore.useDispatch()
  const { id } = useParams<{ id: string }>()
  const isNew = id === 'novo'

  if (!isNew) return null

  return (
    <FieldGroup>
      <Radio.Group
        value={documentMode}
        onChange={(e) => dispatch(ParceiroDetailStore.actions.setDocumentMode(e.target.value))}
      >
        <Radio.Button value="CNPJ">Usar CNPJ</Radio.Button>
        <Radio.Button value="CPF">Usar CPF</Radio.Button>
      </Radio.Group>
    </FieldGroup>
  )
}

const FormLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 500px;
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
`

const FieldError = styled.div`
  color: #ff4d4f;
  font-size: 12px;
`

const SaveButton = styled(Button)`
  align-self: flex-start;
`
